import type React from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { RefObject } from 'react';
import type { AnimatedSvgRef } from '../../../../AnimatedIcon/types';
import type { BuilderAnimationIconComponent } from '../../../Menu.types';

const SELECTION_ANIMATION_SETTLE_DELAY_MS = 0;
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

type UseIconAnimationOptions = {
  BuilderAnimationIcon: BuilderAnimationIconComponent | undefined;
  isCurrentPage: boolean;
  isOpen: boolean;
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
};

type UseIconAnimationResult = {
  iconAnimatedRef: RefObject<AnimatedSvgRef>;
  handleMouseEnter: (event: React.MouseEvent<HTMLDivElement>) => void;
  handleMouseLeave: (event: React.MouseEvent<HTMLDivElement>) => void;
};

export function useIconAnimation({
  BuilderAnimationIcon,
  isCurrentPage,
  isOpen,
  onMouseEnter,
  onMouseLeave,
}: UseIconAnimationOptions): UseIconAnimationResult {
  const iconAnimatedRef = useRef<AnimatedSvgRef>(null);
  const isAnimatedForwardRef = useRef<boolean | null>(null);
  const prevIsImmediateRef = useRef<boolean | null>(null);
  const prevIsSelectedRef = useRef<boolean | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const onMouseEnterRef = useRef(onMouseEnter);
  onMouseEnterRef.current = onMouseEnter;
  const onMouseLeaveRef = useRef(onMouseLeave);
  onMouseLeaveRef.current = onMouseLeave;

  const cancelPending = useCallback(() => {
    if (pendingTimeoutRef.current !== null) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!BuilderAnimationIcon) {
      cancelPending();
      isAnimatedForwardRef.current = null;
      prevIsImmediateRef.current = null;
      prevIsSelectedRef.current = null;
      return cancelPending;
    }

    const applyAnimation = (forward: boolean) => {
      isAnimatedForwardRef.current = forward;
      if (forward) {
        iconAnimatedRef.current?.runAnimationForward();
      } else {
        iconAnimatedRef.current?.runAnimationBackward();
      }
    };

    const isImmediate = isHovered || isOpen;
    const isSelected = isCurrentPage;
    const shouldBeForward = isImmediate || isSelected;

    if (isAnimatedForwardRef.current === null) {
      isAnimatedForwardRef.current = shouldBeForward;
      prevIsImmediateRef.current = isImmediate;
      prevIsSelectedRef.current = isSelected;
      if (shouldBeForward) {
        iconAnimatedRef.current?.runAnimationForward();
      }
      return cancelPending;
    }

    if (prevIsImmediateRef.current !== isImmediate) {
      prevIsImmediateRef.current = isImmediate;
      cancelPending();
      if (isAnimatedForwardRef.current !== shouldBeForward) {
        applyAnimation(shouldBeForward);
      }
      return cancelPending;
    }

    if (prevIsSelectedRef.current === isSelected) {
      cancelPending();
      return cancelPending;
    }

    prevIsSelectedRef.current = isSelected;

    if (isImmediate || isAnimatedForwardRef.current === shouldBeForward) {
      cancelPending();
      return cancelPending;
    }

    cancelPending();
    pendingTimeoutRef.current = setTimeout(() => {
      pendingTimeoutRef.current = null;
      applyAnimation(isSelected);
    }, SELECTION_ANIMATION_SETTLE_DELAY_MS);

    return cancelPending;
  }, [BuilderAnimationIcon, cancelPending, isCurrentPage, isHovered, isOpen]);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      setIsHovered(true);
      onMouseEnterRef.current?.(event);
    },
    [],
  );

  const handleMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      setIsHovered(false);
      onMouseLeaveRef.current?.(event);
    },
    [],
  );

  return { iconAnimatedRef, handleMouseEnter, handleMouseLeave };
}
